import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Banner, Button, DataTable, Panel, Select, Spinner } from "@NP-Webber/crm-kit/dist/next/index.js";
import type { CkDataColumn } from "@NP-Webber/crm-kit/dist/next/index.js";
import { salesApi, type CommissionLine, type CommissionRule, type CommissionsReport } from "../../api/salesApi";
import { ScreenHeader } from "../page/ScreenHeader";
import { errorMessage, fmtDate, fmtIls, fmtNum, monthKey, monthLabel } from "./salesUi";
import "./sales.css";

function rulePercents(r: CommissionRule): string {
  const parts: string[] = [];
  if (r.percent != null) parts.push(`${r.percent}%`);
  if (r.abroadPercent != null && r.abroadPercent !== r.percent) parts.push(`חו״ל ${r.abroadPercent}%`);
  if (r.partialPercent != null) parts.push(`חלקית ${r.partialPercent}%`);
  if (r.netOfVat) parts.push("ללא מע״מ");
  return parts.join(" · ") || "—";
}

const shekels = (agorot: number) => agorot / 100;

/** /sales/commissions — «עמלות נציגים» for a month: bases, percents, commission, monthly base, total. A row opens the rep's deals of the month. */
export function CommissionsPage() {
  const navigate = useNavigate();
  const [month, setMonth] = useState(() => monthKey(-1));
  const [data, setData] = useState<CommissionsReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  const months = useMemo(() => Array.from({ length: 12 }, (_, i) => monthKey(-i)), []);
  const shift = (delta: number) => {
    const [y, m] = month.split("-").map(Number);
    const d = new Date(Date.UTC(y, m - 1 + delta, 1));
    setMonth(d.toISOString().slice(0, 7));
  };

  useEffect(() => {
    let alive = true;
    setData(null);
    salesApi
      .commissions(month)
      .then((r) => {
        if (!alive) return;
        setData(r);
        setError(null);
      })
      .catch((e) => alive && setError(errorMessage(e, "לא הצלחנו לחשב את העמלות")));
    return () => {
      alive = false;
    };
  }, [month]);

  const total = data?.lines.reduce((a, l) => a + l.totalAgorot, 0) ?? 0;
  const columns = useMemo<Array<CkDataColumn<CommissionLine>>>(
    () => [
      { key: "repName", label: "נציג", type: "text", sortable: true, render: (r) => <b className={r.active ? undefined : "sl-muted"}>{r.repName}</b> },
      { key: "deals", label: "עסקאות", type: "number", sortable: true, sum: true },
      { key: "dom", label: "בסיס ישראל", type: "money", sortable: true, sum: true, getValue: (r) => shekels(r.domesticBaseAgorot), render: (r) => <span className="sl-num">{fmtIls(r.domesticBaseAgorot)}</span> },
      { key: "abr", label: "בסיס חו״ל", type: "money", sortable: true, sum: true, getValue: (r) => shekels(r.abroadBaseAgorot), render: (r) => <span className="sl-num">{fmtIls(r.abroadBaseAgorot)}</span> },
      { key: "par", label: "בסיס עסקאות חלקיות", type: "money", sortable: true, sum: true, getValue: (r) => shekels(r.partialBaseAgorot), render: (r) => <span className="sl-num">{fmtIls(r.partialBaseAgorot)}</span> },
      { key: "rule", label: "אחוזים", type: "text", getValue: (r) => rulePercents(r.rule), render: (r) => <span className="sl-muted">{rulePercents(r.rule)}</span> },
      { key: "comm", label: "עמלה", type: "money", sortable: true, sum: true, getValue: (r) => shekels(r.commissionAgorot), render: (r) => <span className="sl-num">{fmtIls(r.commissionAgorot)}</span> },
      {
        key: "base",
        label: "בסיס חודשי",
        type: "money",
        sortable: true,
        sum: true,
        getValue: (r) => shekels(r.monthlyBaseAgorot),
        render: (r) => <span className="sl-num">{r.monthlyBaseAgorot ? fmtIls(r.monthlyBaseAgorot) : "—"}</span>,
      },
      { key: "total", label: "סה״כ לתשלום", type: "money", sortable: true, sum: true, getValue: (r) => shekels(r.totalAgorot), render: (r) => <b className="sl-num">{fmtIls(r.totalAgorot)}</b> },
    ],
    [],
  );
  const openDeals = (r: CommissionLine) => {
    if (!data) return;
    navigate(`/sales/deals?repId=${encodeURIComponent(r.repId)}&from=${data.from}&to=${data.to}`);
  };
  const anyNet = data?.lines.some((l) => l.rule.netOfVat);
  const monthOptions = (months.includes(month) ? months : [month, ...months]).map((m) => ({ value: m, label: monthLabel(m) }));

  return (
    <div className="sl-page">
      <ScreenHeader sub={`דוח עמלות — ${monthLabel(month)}`} />
      <div className="sl-row sl-row--between sl-no-print">
        <div className="sl-row">
          <Button size="sm" icon="right" iconOnly aria-label="החודש הקודם" onClick={() => shift(-1)} />
          <Select aria-label="חודש" options={monthOptions} value={month} onChange={setMonth} />
          <Button size="sm" icon="left" iconOnly aria-label="החודש הבא" disabled={month >= monthKey(0)} onClick={() => shift(1)} />
        </div>
        <Button icon="file" onClick={() => window.print()}>
          הדפסה
        </Button>
      </div>
      <h2 className="sl-print-only">עמלות נציגים — {monthLabel(month)}</h2>
      {error ? <Banner tone="danger">{error}</Banner> : null}
      {!data && !error ? (
        <div className="sl-busy">
          <Spinner size={26} />
        </div>
      ) : null}
      {data ? (
        <Panel flush title={`${fmtDate(data.from)} – ${fmtDate(data.to)}`} sub={`סה״כ לתשלום לנציגים: ${fmtIls(total)}`} icon="coin">
          <DataTable<CommissionLine>
            columns={columns}
            rows={data.lines}
            rowKey="repId"
            storageKey="tb.sales.commissions"
            selectable={false}
            searchPlaceholder="חיפוש נציג…"
            onRowClick={openDeals}
            rowActions={(r) => [{ key: "deals", icon: "list", label: "העסקאות של הנציג בחודש", onClick: () => openDeals(r) }]}
            exportFilename={`עמלות-${month}`}
            exportNote="הייצוא כולל את השורות שבסינון, סכומים בשקלים."
            emptyTitle="אין נציגים"
            emptyText="הוסיפו נציגים בהגדרות המכירות."
          />
          <div className="sl-comm-foot">
            <div>
              <span>עסקאות חלקיות (כל נציג עם אחוז «חלקיות» מקבל ממנה)</span>
              <span className="sl-num">
                {fmtNum(data.partial.deals)} · {fmtIls(data.partial.grossAgorot)}
              </span>
            </div>
            <div>
              <span>דרך הקו (ללא נציג)</span>
              <span className="sl-num">
                {fmtNum(data.selfService.deals)} · {fmtIls(data.selfService.grossAgorot)}
              </span>
            </div>
            <div>
              <span>עסקאות נציג שלא שויכו לנציג</span>
              <span className="sl-num">
                {fmtNum(data.unassigned.deals)} · {fmtIls(data.unassigned.grossAgorot)}
              </span>
            </div>
          </div>
          <p className="sl-note" style={{ padding: "0 14px 12px" }}>
            העמלה מחושבת על סכומי העסקאות בחודש (ללא עסקאות שבוטלו), לפי האחוזים של כל נציג.
            {" "}כשהכלל של הנציג מסומן «ללא מע״מ» — העמלה מחושבת על הסכום בלי המע״מ{anyNet ? " (מסומן בעמודת האחוזים)" : ""}.
          </p>
        </Panel>
      ) : null}
    </div>
  );
}

export default CommissionsPage;
